import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Button } from '@/components/ui/Button';
import { DelegationFormValues } from '@/types/domain/approval';
import { Check, X, Undo, HelpCircle, UserCheck } from 'lucide-react';

const formatInr = (val: number) =>
  `₹${Number(val || 0).toLocaleString('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

// Custom hook to lock body scroll and listen for Escape key
const useModalOverlay = (isOpen: boolean, onClose: () => void) => {
  useEffect(() => {
    if (!isOpen) return;

    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      document.body.style.overflow = originalOverflow;
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, onClose]);
};

// 1. APPROVE MODAL
interface ApproveModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (comments?: string) => Promise<void>;
  reqNumber: string;
  title: string;
  totalAmount: number;
  currency?: string;
  isLoading: boolean;
}

export const ApproveModal: React.FC<ApproveModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  reqNumber,
  title,
  totalAmount,
  isLoading,
}) => {
  const [comment, setComment] = useState('');
  useModalOverlay(isOpen, onClose);

  if (!isOpen || typeof document === 'undefined') return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/75 backdrop-blur-sm p-4 overflow-y-auto animate-in fade-in"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md rounded-2xl border border-border bg-card text-foreground p-6 shadow-2xl space-y-4 font-poppins relative z-50 my-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="p-3 rounded-2xl bg-emerald-500/10 text-emerald-600 border border-emerald-500/20 shrink-0">
              <Check className="h-6 w-6" />
            </div>
            <div>
              <h3 className="font-poppins text-lg font-bold text-foreground">Confirm Authorization</h3>
              <p className="text-xs text-foreground-secondary font-medium">
                Are you sure you want to approve request <strong className="text-foreground">{reqNumber}</strong>?
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1 rounded-lg text-foreground-muted hover:text-foreground hover:bg-muted transition-colors cursor-pointer shrink-0"
            title="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="rounded-xl border border-border p-3.5 bg-muted/40 space-y-1 text-xs font-semibold">
          <p className="text-foreground">{title}</p>
          <p className="text-primary font-extrabold text-base font-mono">{formatInr(totalAmount)}</p>
          <p className="text-[11px] text-foreground-muted font-normal">This approval decision will be permanently recorded in the official audit trail.</p>
        </div>

        <div className="space-y-1.5">
          <label className="text-xs font-bold text-foreground">Optional Approval Remarks</label>
          <textarea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder="Add optional notes for the procurement file..."
            className="w-full rounded-xl border border-input bg-background p-3 text-xs font-medium focus:outline-none focus:ring-2 focus:ring-primary h-20 font-poppins"
          />
        </div>

        <div className="flex items-center justify-end gap-2.5 pt-2 border-t border-border">
          <Button variant="outline" onClick={onClose} disabled={isLoading} className="rounded-xl font-bold font-poppins cursor-pointer">
            Cancel
          </Button>
          <Button
            className="rounded-xl font-bold bg-emerald-600 hover:bg-emerald-700 text-white font-poppins cursor-pointer shadow-sm"
            isLoading={isLoading}
            onClick={() => onConfirm(comment)}
          >
            <Check className="h-4 w-4 mr-1.5" />
            Confirm Approval
          </Button>
        </div>
      </div>
    </div>,
    document.body
  );
};

// 2. REJECT MODAL
interface RejectModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (reason: string) => Promise<void>;
  reqNumber: string;
  isLoading: boolean;
}

export const RejectModal: React.FC<RejectModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  reqNumber,
  isLoading,
}) => {
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  useModalOverlay(isOpen, onClose);

  if (!isOpen || typeof document === 'undefined') return null;

  const handleSubmit = () => {
    if (!reason.trim()) {
      setError('Rejection reason is mandatory and cannot be empty.');
      return;
    }
    setError(null);
    onConfirm(reason.trim());
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/75 backdrop-blur-sm p-4 overflow-y-auto animate-in fade-in"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md rounded-2xl border border-rose-500/30 bg-card text-foreground p-6 shadow-2xl space-y-4 font-poppins relative z-50 my-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="p-3 rounded-2xl bg-rose-500/10 text-rose-600 border border-rose-500/20 shrink-0">
              <X className="h-6 w-6" />
            </div>
            <div>
              <h3 className="font-poppins text-lg font-bold text-rose-700 dark:text-rose-300">Reject Request</h3>
              <p className="text-xs text-foreground-secondary font-medium">
                Rejecting request <strong className="text-foreground">{reqNumber}</strong> will halt the procurement process.
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1 rounded-lg text-foreground-muted hover:text-foreground hover:bg-muted transition-colors cursor-pointer shrink-0"
            title="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="space-y-1.5">
          <label className="text-xs font-bold text-foreground">
            Mandatory Rejection Reason <span className="text-rose-500">*</span>
          </label>
          <textarea
            value={reason}
            onChange={(e) => {
              setReason(e.target.value);
              setError(null);
            }}
            placeholder="Explain why this request is rejected (e.g. Budget constraints, unapproved supplier, non-standard specifications)..."
            className="w-full rounded-xl border border-input bg-background p-3 text-xs font-medium focus:outline-none focus:ring-2 focus:ring-rose-500 h-24 font-poppins"
          />
          {error && <p className="text-[11px] font-bold text-rose-500">{error}</p>}
        </div>

        <div className="flex items-center justify-end gap-2.5 pt-2 border-t border-border">
          <Button variant="outline" onClick={onClose} disabled={isLoading} className="rounded-xl font-bold font-poppins cursor-pointer">
            Cancel
          </Button>
          <Button
            className="rounded-xl font-bold bg-rose-600 hover:bg-rose-700 text-white font-poppins cursor-pointer shadow-sm"
            isLoading={isLoading}
            onClick={handleSubmit}
          >
            <X className="h-4 w-4 mr-1.5" />
            Reject Request
          </Button>
        </div>
      </div>
    </div>,
    document.body
  );
};

// 3. SEND BACK MODAL
interface SendBackModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (explanation: string) => Promise<void>;
  reqNumber: string;
  isLoading: boolean;
}

export const SendBackModal: React.FC<SendBackModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  reqNumber,
  isLoading,
}) => {
  const [comments, setComments] = useState('');
  const [error, setError] = useState<string | null>(null);
  useModalOverlay(isOpen, onClose);

  if (!isOpen || typeof document === 'undefined') return null;

  const handleSubmit = () => {
    if (!comments.trim()) {
      setError('Explanation for sending back is required.');
      return;
    }
    setError(null);
    onConfirm(comments.trim());
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/75 backdrop-blur-sm p-4 overflow-y-auto animate-in fade-in"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md rounded-2xl border border-amber-500/30 bg-card text-foreground p-6 shadow-2xl space-y-4 font-poppins relative z-50 my-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="p-3 rounded-2xl bg-amber-500/10 text-amber-600 border border-amber-500/20 shrink-0">
              <Undo className="h-6 w-6" />
            </div>
            <div>
              <h3 className="font-poppins text-lg font-bold text-amber-700 dark:text-amber-300">Return to Requester</h3>
              <p className="text-xs text-foreground-secondary font-medium">
                Send <strong className="text-foreground">{reqNumber}</strong> back to the employee for correction or missing attachments.
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1 rounded-lg text-foreground-muted hover:text-foreground hover:bg-muted transition-colors cursor-pointer shrink-0"
            title="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="space-y-1.5">
          <label className="text-xs font-bold text-foreground">
            Correction Instructions / Explanation <span className="text-amber-500">*</span>
          </label>
          <textarea
            value={comments}
            onChange={(e) => {
              setComments(e.target.value);
              setError(null);
            }}
            placeholder="Specify what needs to be revised by the requester..."
            className="w-full rounded-xl border border-input bg-background p-3 text-xs font-medium focus:outline-none focus:ring-2 focus:ring-amber-500 h-24 font-poppins"
          />
          {error && <p className="text-[11px] font-bold text-rose-500">{error}</p>}
        </div>

        <div className="flex items-center justify-end gap-2.5 pt-2 border-t border-border">
          <Button variant="outline" onClick={onClose} disabled={isLoading} className="rounded-xl font-bold font-poppins cursor-pointer">
            Cancel
          </Button>
          <Button
            className="rounded-xl font-bold bg-amber-600 hover:bg-amber-700 text-white font-poppins cursor-pointer shadow-sm"
            isLoading={isLoading}
            onClick={handleSubmit}
          >
            <Undo className="h-4 w-4 mr-1.5" />
            Send Back to Requester
          </Button>
        </div>
      </div>
    </div>,
    document.body
  );
};

// 4. REQUEST CLARIFICATION MODAL
interface ClarificationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (question: string) => Promise<void>;
  reqNumber: string;
  isLoading: boolean;
}

export const ClarificationModal: React.FC<ClarificationModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  reqNumber,
  isLoading,
}) => {
  const [question, setQuestion] = useState('');
  const [error, setError] = useState<string | null>(null);
  useModalOverlay(isOpen, onClose);

  if (!isOpen || typeof document === 'undefined') return null;

  const handleSubmit = () => {
    if (!question.trim()) {
      setError('Clarification query cannot be empty.');
      return;
    }
    setError(null);
    onConfirm(question.trim());
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/75 backdrop-blur-sm p-4 overflow-y-auto animate-in fade-in"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md rounded-2xl border border-primary/30 bg-card text-foreground p-6 shadow-2xl space-y-4 font-poppins relative z-50 my-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="p-3 rounded-2xl bg-primary/10 text-primary border border-primary/20 shrink-0">
              <HelpCircle className="h-6 w-6" />
            </div>
            <div>
              <h3 className="font-poppins text-lg font-bold text-foreground">Request Clarification</h3>
              <p className="text-xs text-foreground-secondary font-medium">
                Ask a question regarding request <strong className="text-foreground">{reqNumber}</strong>.
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1 rounded-lg text-foreground-muted hover:text-foreground hover:bg-muted transition-colors cursor-pointer shrink-0"
            title="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="space-y-1.5">
          <label className="text-xs font-bold text-foreground">
            Clarification Query <span className="text-primary">*</span>
          </label>
          <textarea
            value={question}
            onChange={(e) => {
              setQuestion(e.target.value);
              setError(null);
            }}
            placeholder="Type your question for the requester or engineering team..."
            className="w-full rounded-xl border border-input bg-background p-3 text-xs font-medium focus:outline-none focus:ring-2 focus:ring-primary h-24 font-poppins"
          />
          {error && <p className="text-[11px] font-bold text-rose-500">{error}</p>}
        </div>

        <div className="flex items-center justify-end gap-2.5 pt-2 border-t border-border">
          <Button variant="outline" onClick={onClose} disabled={isLoading} className="rounded-xl font-bold font-poppins cursor-pointer">
            Cancel
          </Button>
          <Button
            className="rounded-xl font-bold bg-primary hover:bg-primary/90 text-primary-foreground font-poppins cursor-pointer shadow-sm"
            isLoading={isLoading}
            onClick={handleSubmit}
          >
            <HelpCircle className="h-4 w-4 mr-1.5" />
            Submit Query
          </Button>
        </div>
      </div>
    </div>,
    document.body
  );
};

// 5. DELEGATE MODAL
interface DelegateModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (payload: DelegationFormValues) => Promise<void>;
  reqNumber: string;
  isLoading: boolean;
}

export const DelegateModal: React.FC<DelegateModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  reqNumber,
  isLoading,
}) => {
  const [delegateToUserEmail, setDelegateToUserEmail] = useState('rachel.chen@procureai.enterprise');
  const [startDate, setStartDate] = useState(new Date().toISOString().split('T')[0]);
  const [endDate, setEndDate] = useState(
    new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
  );
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  useModalOverlay(isOpen, onClose);

  if (!isOpen || typeof document === 'undefined') return null;

  const handleSubmit = () => {
    if (!delegateToUserEmail || !startDate || !endDate || !reason.trim()) {
      setError('All fields are required for delegation.');
      return;
    }
    if (new Date(endDate!) < new Date(startDate!)) {
      setError('End Date cannot precede Start Date.');
      return;
    }
    setError(null);
    onConfirm({
      delegateToUserEmail,
      startDate: startDate!,
      endDate: endDate!,
      reason: reason.trim(),
    });
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/75 backdrop-blur-sm p-4 overflow-y-auto animate-in fade-in"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md rounded-2xl border border-purple-500/30 bg-card text-foreground p-6 shadow-2xl space-y-4 font-poppins relative z-50 my-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="p-3 rounded-2xl bg-purple-500/10 text-purple-600 border border-purple-500/20 shrink-0">
              <UserCheck className="h-6 w-6" />
            </div>
            <div>
              <h3 className="font-poppins text-lg font-bold text-purple-700 dark:text-purple-300">Delegate Authority</h3>
              <p className="text-xs text-foreground-secondary font-medium">
                Temporarily assign approval authority for <strong className="text-foreground">{reqNumber}</strong>.
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1 rounded-lg text-foreground-muted hover:text-foreground hover:bg-muted transition-colors cursor-pointer shrink-0"
            title="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="space-y-3 text-xs">
          <div className="space-y-1">
            <label className="font-bold text-foreground">Delegate To (Authorized Approver Email)</label>
            <select
              value={delegateToUserEmail}
              onChange={(e) => setDelegateToUserEmail(e.target.value)}
              className="w-full rounded-xl border border-input bg-background p-2.5 text-xs font-semibold focus:ring-2 focus:ring-purple-500 font-poppins"
            >
              <option value="rachel.chen@procureai.enterprise">Rachel Chen (Finance Manager)</option>
              <option value="david.sterling@procureai.enterprise">David Sterling (Procurement Manager)</option>
              <option value="alexander.hamilton@procureai.enterprise">Alexander Hamilton (VP Operations)</option>
            </select>
          </div>

          <div className="grid grid-cols-2 gap-2.5">
            <div className="space-y-1">
              <label className="font-bold text-foreground">Start Date</label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="w-full rounded-xl border border-input bg-background p-2.5 text-xs font-semibold focus:ring-2 focus:ring-purple-500 font-poppins"
              />
            </div>
            <div className="space-y-1">
              <label className="font-bold text-foreground">End Date</label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="w-full rounded-xl border border-input bg-background p-2.5 text-xs font-semibold focus:ring-2 focus:ring-purple-500 font-poppins"
              />
            </div>
          </div>

          <div className="space-y-1">
            <label className="font-bold text-foreground">Delegation Reason / Business Justification</label>
            <textarea
              value={reason}
              onChange={(e) => {
                setReason(e.target.value);
                setError(null);
              }}
              placeholder="Planned absence / Travel coverage..."
              className="w-full rounded-xl border border-input bg-background p-3 text-xs font-medium focus:ring-2 focus:ring-purple-500 h-20 font-poppins"
            />
          </div>

          {error && <p className="text-[11px] font-bold text-rose-500">{error}</p>}
        </div>

        <div className="flex items-center justify-end gap-2.5 pt-2 border-t border-border">
          <Button variant="outline" onClick={onClose} disabled={isLoading} className="rounded-xl font-bold font-poppins cursor-pointer">
            Cancel
          </Button>
          <Button
            className="rounded-xl font-bold bg-purple-600 hover:bg-purple-700 text-white font-poppins cursor-pointer shadow-sm"
            isLoading={isLoading}
            onClick={handleSubmit}
          >
            <UserCheck className="h-4 w-4 mr-1.5" />
            Assign Delegation
          </Button>
        </div>
      </div>
    </div>,
    document.body
  );
};
